import React, { useEffect, useRef, useState } from "react";
import Button from "../../../../atoms/Button/Button";
import logo from "../../../../../assets/pics/logo.png";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { AiOutlineMenu, AiOutlineClose } from "react-icons/ai";
import nationalorg from "../../../../../assets/pics/footer/white.png";
import { useTranslation } from "react-i18next";

export default function Navbar() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const skipUrlLanguageSync = useRef(false);

  const toggleMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  useEffect(() => {
    const urlLang = new URLSearchParams(window.location.search)
      .get("lang")
      ?.toLowerCase();
    // Pages opened from the map carry ?lang= for this tab only.
    // Everywhere else, remember the language currently on screen so a new
    // character tab follows this visit instead of an older one.
    if (urlLang !== "en" && urlLang !== "ar") {
      const lang = (i18n.language || "ar").split("-")[0];
      if (lang === "en" || lang === "ar") {
        localStorage.setItem("language", lang);
      }
    }
  }, [i18n.language]);

  useEffect(() => {
    if (skipUrlLanguageSync.current) {
      skipUrlLanguageSync.current = false;
      return;
    }

    const lang = new URLSearchParams(window.location.search)
      .get("lang")
      ?.toLowerCase();
    if (
      (lang === "en" || lang === "ar") &&
      (i18n.language?.split("-")[0] || "ar") !== lang
    ) {
      i18n.changeLanguage(lang);
    }
    // Apply ?lang= when the address changes, not when i18n changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const changeLanguage = (language) => {
    console.log("language button clicked", language);
    console.log("address before click", window.location.href);

    const url = new URL(window.location.href);
    const langKeys = [...url.searchParams.keys()].filter(
      (key) => key.toLowerCase() === "lang"
    );
    const urlLang = langKeys.length
      ? url.searchParams.get(langKeys[0])?.toLowerCase()
      : null;
    console.log("lang query before click", urlLang);

    if ((urlLang === "en" || urlLang === "ar") && urlLang !== language) {
      skipUrlLanguageSync.current = true;
      langKeys.forEach((key) => url.searchParams.delete(key));
      url.searchParams.set("lang", language);
      const next = `${url.pathname}${url.search}${url.hash}`;
      console.log("updating address to", next);
      window.history.replaceState(window.history.state, "", next);
      navigate(next, { replace: true });
    } else if (urlLang === "en" || urlLang === "ar") {
      console.log("address already uses", urlLang);
    } else {
      console.log("no lang query on this page, address left unchanged");
    }

    localStorage.setItem("language", language);
    i18n.changeLanguage(language);
    console.log("language applied", language, "address now", window.location.href);
  };

  const currentLang = i18n.language?.split("-")[0] || "ar";
  const isArabic = currentLang === "ar";

  return (
    <nav className="fixed inset-x-0 top-0 z-50 flex w-full min-w-0 max-w-[100vw] items-center justify-between bg-gradient-to-b from-black to-transparent px-2 py-2 md:p-4">
      <div className="mx-0 flex shrink-0 items-center justify-center md:mx-1">
        <div className="me-2 w-8 shrink-0 md:me-0 md:w-10">
          <img src={logo} alt="logo Pic" className="h-auto w-full object-contain" />
        </div>

        <div className="block shrink-0 md:hidden">
          <button onClick={toggleMenu} className="text-2xl leading-none text-white">
            {isMobileMenuOpen ? <AiOutlineClose /> : <AiOutlineMenu />}
          </button>
        </div>

        <div className="hidden md:block">
          <div className="text-white font-reem-kufi-semibold mx-3 text-base">
            {t("nav.beta")}
          </div>
        </div>
      </div>

      <ul
        className={`${
          isMobileMenuOpen ? "flex" : "hidden"
        } rounded bg-gray-300 flex-col gap-y-2 z-20 text-white font-reem-kufi-semibold tracking-wider md:bg-transparent absolute top-full inset-x-0 w-full md:flex md:flex-row md:gap-x-6 md:relative md:w-auto md:top-auto md:inset-x-auto`}
      >
        <li className="hover:text-amber border-b border-gray-400 md:border-none px-4 md:px-0">
          <NavLink
            to="/"
            className={({ isActive }) => (isActive ? "text-amber" : "")}
          >
            {t("nav.home")}
          </NavLink>
        </li>
        <li className="hover:text-amber border-b border-gray-400 md:border-none px-4 md:px-0">
          <NavLink
            to="/characters"
            className={({ isActive }) => (isActive ? "text-amber" : "")}
          >
            {t("nav.characters")}
          </NavLink>
        </li>
        <li className="hover:text-amber border-b border-gray-400 md:border-none px-4 md:px-0">
          <NavLink
            to="/fields"
            className={({ isActive }) => (isActive ? "text-amber" : "")}
          >
            {t("nav.fields")}
          </NavLink>
        </li>
        <li className="hover:text-amber border-b border-gray-400 md:border-none px-4 md:px-0">
          <NavLink
            to="/places"
            className={({ isActive }) => (isActive ? "text-amber" : "")}
          >
            {t("nav.places")}
          </NavLink>
        </li>
        <li className="hover:text-amber border-b border-gray-400 md:border-none px-4 md:px-0">
          <NavLink
            to="/about"
            className={({ isActive }) => (isActive ? "text-amber" : "")}
          >
            {t("nav.about")}
          </NavLink>
        </li>
      </ul>

      <div className="flex shrink-0 items-center gap-1 md:gap-4">
        <div className="mx-1 w-[40px] shrink-0 overflow-hidden sm:w-[50px] md:mx-2 md:w-[60px]">
          <img
            src={nationalorg}
            alt="national organization"
            className="h-auto w-full object-contain"
          />
        </div>
        <div className="flex items-center gap-1 md:gap-2 font-cairo-semibold">
          <Button
            primary
            rounded
            outline={!isArabic}
            className="!w-[88px] !min-w-[88px] !h-7 !justify-center !box-border"
            onClick={() => {
              console.log("Arabic button click event fired");
              changeLanguage("ar");
            }}
          >
            {t("nav.langAr")}
          </Button>
          <Button
            primary
            rounded
            outline={isArabic}
            className="!w-[88px] !min-w-[88px] !h-7 !justify-center !box-border"
            onClick={() => {
              console.log("English button click event fired");
              changeLanguage("en");
            }}
          >
            {t("nav.langEn")}
          </Button>
        </div>
      </div>
    </nav>
  );
}
